import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { userSchema, type User } from '@/lib/auth';
import {
  okSchema,
  passwordChangeSchema,
  profileUpdateSchema,
  sessionsResponseSchema,
  type NotifPrefs,
  type PasswordChangeInput,
  type ProfileUpdateInput,
} from './schemas';

// Query keys do módulo. Sempre estruturadas como ['perfil', '<resource>', ...].
const keys = {
  all: ['perfil'] as const,
  sessions: (userId: string) => ['perfil', 'sessions', userId] as const,
};

export function useUpdateProfile(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProfileUpdateInput) => {
      const body = profileUpdateSchema.parse(input);
      return api(`/api/users/${userId}`, {
        method: 'PATCH',
        body,
        schema: userSchema,
      });
    },
    onSuccess: (user) => {
      qc.setQueryData(['auth', 'me'], user);
    },
  });
}

export function useUpdatePreferences(userId: string, current: User) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: Partial<NotifPrefs>) => {
      const merged = { ...(current.preferences ?? {}), ...prefs };
      return api(`/api/users/${userId}`, {
        method: 'PATCH',
        body: { preferences: merged },
        schema: userSchema,
      });
    },
    onSuccess: (user) => {
      qc.setQueryData(['auth', 'me'], user);
    },
  });
}

export function useChangePassword(userId: string) {
  return useMutation({
    mutationFn: async (input: PasswordChangeInput) => {
      const parsed = passwordChangeSchema.parse(input);
      return api(`/api/users/${userId}/password`, {
        method: 'PATCH',
        body: {
          current_password: parsed.current_password,
          new_password: parsed.new_password,
        },
        schema: okSchema,
      });
    },
  });
}

export function useSessions(userId: string) {
  return useQuery({
    queryKey: keys.sessions(userId),
    queryFn: ({ signal }) =>
      api(`/api/users/${userId}/sessions`, { schema: sessionsResponseSchema, signal }),
  });
}

export function useEndOtherSessions(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api(`/api/users/${userId}/sessions`, {
        method: 'DELETE',
        schema: okSchema.extend({ deleted: okSchema.shape.ok.optional() }).partial(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sessions(userId) });
    },
  });
}
